import { Link } from "@tanstack/react-router";

import { ArrowIcon } from "@/components/icons/ArrowIcon";
import type { Categoria, Listagem, PostResumo } from "@/lib/blog.functions";
import { VIDEOS } from "@/lib/itv/videos";

/** Data no formato do leitor. Fuso fixo para o servidor e o navegador baterem. */
function formatarData(valor: string | null): string | null {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(data);
}

function resumoDe(post: PostResumo): string | null {
  return post.subtitle || post.direct_answer || null;
}

/** O artigo ainda não tem página própria; o caminho final é /blog/{slug}/. */
function caminhoDoArtigo(slug: string) {
  return `/blog/${slug}/`;
}

function Breadcrumb({ trilha }: { trilha: Array<{ nome: string; path: string }> }) {
  return (
    <nav className="blog-breadcrumb" aria-label="Você está aqui">
      <ol className="t-label-nav uppercase">
        {trilha.map((item, indice) => (
          <li key={`${item.path}-${item.nome}`}>
            {indice > 0 ? <span aria-hidden="true">›</span> : null}
            {indice < trilha.length - 1 ? <a href={item.path}>{item.nome}</a> : <span>{item.nome}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function BarraCategorias({
  categorias,
  ativa,
}: {
  categorias: Categoria[];
  ativa: string | null;
}) {
  return (
    <div className="blog-categories">
      <div className="blog-container">
        <nav aria-label="Categorias do blog" className="blog-category-row">
          <Link
            to="/blog/"
            className={`blog-category-link ${ativa === null ? "is-active" : ""} t-label-nav uppercase`}
            aria-current={ativa === null ? "page" : undefined}
          >
            Todos
          </Link>
          {categorias.map((categoria) => (
            <Link
              key={categoria.slug}
              to="/blog/$categoria/"
              params={{ categoria: categoria.slug }}
              className={`blog-category-link ${ativa === categoria.slug ? "is-active" : ""} t-label-nav uppercase`}
              aria-current={ativa === categoria.slug ? "page" : undefined}
            >
              {categoria.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}

function Capa({
  post,
  altura,
}: {
  post: PostResumo;
  altura: string;
}) {
  if (!post.capa) {
    return <div className={`${altura} w-full bg-surface-image`} aria-hidden="true" />;
  }
  return (
    <img
      src={post.capa.path}
      alt={post.capa.alt}
      width={post.capa.width ?? 800}
      height={post.capa.height ?? 450}
      loading="lazy"
      className={`${altura} w-full bg-surface-image object-cover`}
    />
  );
}

export function CardArtigo({ post, variant = "default" }: { post: PostResumo; variant?: "default" | "treatment" }) {
  const data = formatarData(post.published_at);
  const resumo = resumoDe(post);

  if (variant === "treatment") {
    return (
      <article className="treatment-related-card group relative">
        <Capa post={post} altura="treatment-related-image" />
        <div className="treatment-related-copy">
          {post.categoria ? <span className="t-label-card treatment-related-label">Artigo · {post.categoria.label}</span> : null}
          <h3 className="t-treatment-related-title">
            <a href={caminhoDoArtigo(post.slug)} className="after:absolute after:inset-0 focus-visible:outline-none group-focus-within:underline">
              {post.title}
            </a>
          </h3>
          {resumo ? <p className="t-treatment-related-excerpt">{resumo}</p> : null}
        </div>
      </article>
    );
  }

  return (
    <article className="blog-article-card">
      <Capa post={post} altura="blog-card-image" />
      <div className="blog-card-meta">
          {post.categoria ? (
            <span className="t-label-card uppercase text-gold-dark">{post.categoria.label}</span>
          ) : (
            <span />
          )}
          {data ? <span className="t-card-meta text-text-faint">{data}</span> : null}
      </div>
      <h2>
        <a href={caminhoDoArtigo(post.slug)}>{post.title}</a>
      </h2>
      {resumo ? <p className="blog-card-excerpt t-card-excerpt">{resumo}</p> : null}
      <span className="blog-card-link t-link-inline inline-flex items-center gap-[calc(var(--space-16)/4)]">
        Ler artigo <ArrowIcon />
      </span>
    </article>
  );
}

function CardDestaque({ post, grande }: { post: PostResumo; grande?: boolean }) {
  const data = formatarData(post.published_at);

  return (
    <article className={`blog-feature-card ${grande ? "is-main" : ""}`}>
      {post.capa ? (
        <img
          src={post.capa.path}
          alt={post.capa.alt}
          width={post.capa.width ?? 820}
          height={post.capa.height ?? 420}
          loading="lazy"
          className="blog-feature-image"
        />
      ) : null}
      <div className="blog-feature-copy">
        {post.categoria ? (
          <span className="t-label-card uppercase">{grande ? "Em destaque · " : ""}{post.categoria.label}</span>
        ) : null}
        {grande ? <h2>
          <a href={caminhoDoArtigo(post.slug)} className="after:absolute after:inset-0">
            {post.title}
          </a>
        </h2> : <h3><a href={caminhoDoArtigo(post.slug)}>{post.title}</a></h3>}
        {grande ? (
          <p className="blog-feature-meta t-card-meta uppercase">
            {[
              post.reading_minutes ? `${post.reading_minutes} min de leitura` : null,
              data,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function Destaques({ posts }: { posts: PostResumo[] }) {
  if (posts.length === 0) return null;
  const [principal, ...secundarios] = posts;
  if (!principal) return null;

  return (
    <section className="blog-feature-grid" aria-label="Artigos em destaque">
      <CardDestaque post={principal} grande />
      {secundarios.length > 0 ? <div className="blog-feature-side">
        {secundarios.slice(0, 3).map((post) => (
          <CardDestaque key={post.slug} post={post} />
        ))}
      </div> : null}
    </section>
  );
}

function VideosDoBlog() {
  const videos = [...VIDEOS].sort((a, b) => a.ordem - b.ordem).slice(0, 3);

  return (
    <section className="blog-videos" aria-labelledby="blog-videos-title">
      <div className="blog-container">
        <div className="blog-videos-head">
          <p className="blog-video-label t-label-nav uppercase">Conteúdo em vídeo</p>
          <h2 id="blog-videos-title">Assista no YouTube</h2>
        </div>
        <div className="blog-video-grid">
          {videos.map((video) => (
            <article className="blog-video-card" key={video.youtubeId}>
              <a className="blog-video-cover" href={`https://www.youtube.com/watch?v=${video.youtubeId}`}>
                <img src={`/img/videos/${video.youtubeId}.webp`} alt={video.titulo} width={370} height={208} loading="lazy" />
                <img className="blog-video-play" src="/icones/youtube-assistir.svg" alt="" width={60} height={42} loading="lazy" />
                <span className="blog-video-duration t-card-meta" aria-hidden="true">{video.duracao}</span>
              </a>
              <h3><a href={`https://www.youtube.com/watch?v=${video.youtubeId}`}>{video.titulo}</a></h3>
              <p className="blog-video-meta t-card-meta">{video.viewsRotulo} · {video.pilar}</p>
            </article>
          ))}
        </div>
        <div className="blog-videos-footer">
          <a className="btn-contorno btn-contorno-claro" href="/videos/">Ver todos os vídeos</a>
        </div>
      </div>
    </section>
  );
}

type LinkDaPagina = { numero: number; props: Record<string, unknown> };

function Paginacao({
  pagina,
  totalPaginas,
  categoria,
}: {
  pagina: number;
  totalPaginas: number;
  categoria: string | null;
}) {
  if (totalPaginas <= 1) return null;

  const numeros: Array<number | "..."> = [];
  for (let n = 1; n <= totalPaginas; n += 1) {
    if (n === 1 || n === totalPaginas || Math.abs(n - pagina) <= 1) {
      numeros.push(n);
    } else if (numeros[numeros.length - 1] !== "...") {
      numeros.push("...");
    }
  }

  const itemBase =
    "t-label-nav uppercase flex h-[40px] min-w-[40px] items-center justify-center rounded-medium px-[calc(var(--space-16)/2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark";

  function LinkPagina({
    numero,
    children,
    ativo,
  }: {
    numero: number;
    children: React.ReactNode;
    ativo?: boolean;
  }) {
    const classe = `${itemBase} ${ativo ? "bg-gold-dark text-neutral-white" : "text-text-faint hover:text-text-body"}`;
    if (categoria) {
      return numero === 1 ? (
        <Link to="/blog/$categoria/" params={{ categoria }} className={classe}>
          {children}
        </Link>
      ) : (
        <Link
          to="/blog/$categoria/pagina/$n/"
          params={{ categoria, n: String(numero) }}
          className={classe}
        >
          {children}
        </Link>
      );
    }
    return numero === 1 ? (
      <Link to="/blog/" className={classe}>
        {children}
      </Link>
    ) : (
      <Link to="/blog/pagina/$n/" params={{ n: String(numero) }} className={classe}>
        {children}
      </Link>
    );
  }

  return (
    <nav aria-label="Paginação" className="flex items-center justify-between gap-16">
      <div>
        {pagina > 1 ? (
          <LinkPagina numero={pagina - 1}>Anterior</LinkPagina>
        ) : (
          <span className={`${itemBase} text-text-faint/50`}>Anterior</span>
        )}
      </div>
      <div className="flex items-center gap-[calc(var(--space-16)/4)]">
        {numeros.map((item, indice) =>
          item === "..." ? (
            <span key={`reticencias-${indice}`} className={`${itemBase} text-text-faint`}>
              …
            </span>
          ) : (
            <LinkPagina key={item} numero={item} ativo={item === pagina}>
              {String(item)}
            </LinkPagina>
          ),
        )}
      </div>
      <div>
        {pagina < totalPaginas ? (
          <LinkPagina numero={pagina + 1}>Próximo</LinkPagina>
        ) : (
          <span className={`${itemBase} text-text-faint/50`}>Próximo</span>
        )}
      </div>
    </nav>
  );
}

export type BlogListingProps = {
  dados: Listagem;
  h1: string;
  subtitulo: string | null;
  trilha: Array<{ nome: string; path: string }>;
};

export function BlogListing({ dados, h1, subtitulo, trilha }: BlogListingProps) {
  const semArtigos = dados.posts.length === 0 && dados.destaques.length === 0;
  const destaques = dados.destaques.length > 0 ? dados.destaques : dados.posts.slice(0, 4);
  const postsDaGrade = dados.destaques.length > 0 ? dados.posts : dados.posts.slice(destaques.length);
  const tituloPrincipal = h1 === "Aprenda com quem trata.";

  return (
    <main className="blog-page">
      <header className="blog-hero">
        <div className="blog-container blog-hero-inner">
          <Breadcrumb trilha={trilha} />
          <p className="blog-hero-label t-label-nav uppercase">Conteúdo</p>
          <h1>{tituloPrincipal ? <>Aprenda com <em>quem trata.</em></> : h1}</h1>
          {subtitulo ? <p className="blog-hero-copy t-body-lg">{subtitulo}</p> : null}
        </div>
      </header>

      <BarraCategorias categorias={dados.categorias} ativa={dados.categoria?.slug ?? null} />

      <div className="blog-container blog-content">
        <Destaques posts={destaques} />

        {semArtigos ? (
          <p className="blog-empty t-body-lg">
            Ainda não há artigos publicados nesta seção. Em breve, novos conteúdos assinados pelo
            Dr. Thiago Viana.
          </p>
        ) : postsDaGrade.length > 0 ? (
          <div className="blog-grid">
            {postsDaGrade.map((post) => (
              <CardArtigo key={post.slug} post={post} />
            ))}
          </div>
        ) : null}

        <Paginacao
          pagina={dados.pagina}
          totalPaginas={dados.totalPaginas}
          categoria={dados.categoria?.slug ?? null}
        />
      </div>
      {!dados.categoria && dados.pagina === 1 ? <VideosDoBlog /> : null}
    </main>
  );
}

export default BlogListing;
